import { useState } from "react";
import { SeoLocale } from "../../http/protocol";
import { defaultFirst } from "./seoEditorUtils";
import { SeoLocalePanel } from "./SeoLocalePanel";
import { SeoLocaleTabs } from "./SeoLocaleTabs";
import { SeoFallbackInputsByLocaleType, SeoField, SeoMetadataByLocaleType } from "./seoTypes";
import "./seo.scss";

export interface SeoMetadataEditorProps {
  /** The locales the operator serves, one tab each; the default locale's tab comes first. */
  locales: SeoLocale[];
  values: SeoMetadataByLocaleType;
  /** What each locale falls back to, without this target's own values. */
  fallbackInputs: SeoFallbackInputsByLocaleType;
  /**
   * False when the CMS could not be read, so the fallbacks shown may be overridden by CMS SEO. Left out
   * for a target that has no CMS SEO at all, which nothing can override.
   */
  cmsAvailable?: boolean;
  /** The page address the search result preview shows. */
  displayUrl: string;
  onChange: (locale: string, field: SeoField, value: string) => void;
  /** Keeps test ids unique when several editors share a page. */
  testIdPrefix?: string;
}

/**
 * Edits the SEO title, description, keywords and OG image of one target, one tab per locale. It holds no
 * SEO state of its own and has no save action: the host owns the values and decides when to
 * persist them; only the selected tab is local. Every empty field shows what will be emitted in its
 * place, so the operator sees the effect of leaving it empty before saving.
 */
export function SeoMetadataEditor({
  locales,
  values,
  fallbackInputs,
  cmsAvailable,
  displayUrl,
  onChange,
  testIdPrefix = "seo",
}: SeoMetadataEditorProps): JSX.Element {
  const orderedLocales = defaultFirst(locales);
  const defaultCode = orderedLocales[0]?.code ?? "";
  const [selectedCode, setSelectedCode] = useState(defaultCode);
  // Derived rather than reset in an effect, so a locale that leaves the list never renders an empty panel.
  const activeLocale = orderedLocales.find((locale) => locale.code === selectedCode) ?? orderedLocales[0];
  const activeCode = activeLocale?.code ?? "";
  const panelId = `${testIdPrefix}-panel`;

  return (
    <div
      className="bg-white border rounded p-3"
      data-testid={`${testIdPrefix}-editor`}
    >
      <SeoLocaleTabs
        locales={orderedLocales}
        values={values}
        activeCode={activeCode}
        panelId={panelId}
        onSelect={setSelectedCode}
        testIdPrefix={testIdPrefix}
      />
      {activeLocale !== undefined && (
        <div
          id={panelId}
          role="tabpanel"
          aria-labelledby={`${testIdPrefix}-tab-${activeCode}`}
        >
          <SeoLocalePanel
            key={activeCode}
            locale={activeLocale}
            value={values[activeCode] ?? {}}
            fallbackInputs={fallbackInputs[activeCode] ?? {}}
            cmsAvailable={cmsAvailable}
            displayUrl={displayUrl}
            onChange={onChange}
            testIdPrefix={testIdPrefix}
          />
        </div>
      )}
    </div>
  );
}
